/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import libraries */
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { getMuensterBaseSensorTypes } from '@/lib/sensors/muenster/muensterBaseSensorTypes';
import {
    getMuensterDeviceCategory,
    getMuensterDeviceCategoryLabel,
} from '@/lib/sensors/muenster/muensterDeviceCategory';
import { MonitoringUrl } from '@/lib/sensors/urls/monitoringUrl';

/* Import types */
import type { DeviceSensorGroup, DeviceSensorMetadataGroup, MuensterDeviceCategory } from '@/types/deviceSensor';

export class DeviceSensor {
    private readonly catalog: DeviceSensorCatalog;

    private readonly realDeviceName: string;

    private readonly virtualDeviceName: string;

    constructor(
        deviceName: string,
        catalog: DeviceSensorCatalog = new DeviceSensorCatalog(),
    ) {
        const resolvedDeviceNames = catalog.resolveDeviceNames(deviceName);

        this.catalog = catalog;
        this.realDeviceName = resolvedDeviceNames.realDeviceName;
        this.virtualDeviceName = resolvedDeviceNames.virtualDeviceName;
    }

    getDeviceDescription(): string | null {
        return this.catalog.getDeviceDescription(this.virtualDeviceName);
    }

    getDeviceName(): string | null {
        return this.catalog.getSensorMetadata(this.virtualDeviceName)?.name ?? null;
    }

    getTrainingEnd(group: DeviceSensorMetadataGroup): string | null {
        return this.catalog.getSensorMetadata(this.virtualDeviceName, group)?.trainEnd ?? null;
    }

    getSensorGroups(): string[] {
        return this.catalog.getSensorGroups(this.virtualDeviceName);
    }

    getSensorTypes(group: DeviceSensorGroup): string[] {
        if (group === null && this.virtualDeviceName.startsWith('Mue.')) {
            return getMuensterBaseSensorTypes(
                this.catalog.getDeviceMetadataGroups(this.virtualDeviceName),
            );
        }

        return this.catalog.getSensorTypes(group);
    }

    getDeviceCategory(): MuensterDeviceCategory | null {
        return getMuensterDeviceCategory(this.virtualDeviceName);
    }

    getDeviceCategoryLabel(): string | null {
        return getMuensterDeviceCategoryLabel(this.getDeviceCategory());
    }

    getDeviceSensorPart(): string {
        if (!this.virtualDeviceName.startsWith('Mue.')) {
            return this.virtualDeviceName;
        }

        return this.virtualDeviceName.split('.').slice(2).join('.');
    }

    getSensorName(group: DeviceSensorGroup = null, sensor: string | null = null): string | null {
        if (this.virtualDeviceName.startsWith('Mue')) {
            if (!group) {
                if (!sensor) {
                    return this.virtualDeviceName;
                }

                // Münster exposes PV power as P, while Neutraubling uses P_15m.
                const resolvedSensorType = sensor === 'P_15m' ? 'P' : sensor;

                if (this.getSensorTypes(null).includes(resolvedSensorType)) {
                    return `${this.virtualDeviceName}.${resolvedSensorType}`;
                }

                return null;
            }

            if (group === 'DTC') {
                return null;
            }

            if (!sensor) {
                return `calc.AD.${this.virtualDeviceName}.${group}`;
            }

            if (group === 'PV') {
                return `calc.AD.${this.virtualDeviceName}.PV.${sensor}`;
            }

            return `calc.AD.${this.virtualDeviceName}.${group}.${sensor}`;
        }

        if (group === 'DTC') {
            return `${this.realDeviceName}.DTC.total`;
        }

        if (!group) {
            if (sensor === 'W') {
                return `${this.virtualDeviceName}.W_1d`;
            }

            return sensor ? `${this.virtualDeviceName}.${sensor}` : this.virtualDeviceName;
        }

        if (!sensor) {
            return `calc.AD.${this.virtualDeviceName}.${group}`;
        }

        if (group === 'PV') {
            const devicePrefix = `${this.realDeviceName}.P`;
            return `calc.AD.${devicePrefix}.PV.${sensor}`;
        }

        return `calc.AD.${this.virtualDeviceName}.${group}.${sensor}`;
    }

    getSensorUrl(group: DeviceSensorGroup = null, sensor: string | null = null): string | null {
        const sensorName = this.getSensorName(group, sensor);

        return new MonitoringUrl(sensorName).getUrl();
    }
}
